import api from './api';

export interface AdminStats {
  totalGames: number;
  lastUpdated: string;
}

export interface SeedResponse {
  success: boolean;
  message: string;
  data?: {
    gamesSeeded?: number;
  };
}

export interface GamePixImportOptions {
  sid: string;
  maxPages?: number;
  gamesPerPage?: number;
  filterByQuality?: number;
  category?: string;
  orientation?: 'landscape' | 'portrait' | 'all';
  orderBy?: 'pubdate' | 'default';
}

export interface GameMonetizeImportOptions {
  name?: string;
  category?: string;
  company?: string;
  platform?: string;
  links?: number;
  maxGames?: number;
  maxPages?: number;
}

export interface GameDistributionImportOptions {
  collection?: string;
  category?: string;
  mobile?: boolean;
  maxGames?: number;
  maxPages?: number;
}

export interface GameImportResponse {
  success: boolean;
  message: string;
  imported: number;
  failed: number;
}

export const adminAPI = {
  // Seed games from SQL file
  seedGames: async (): Promise<SeedResponse> => {
    const response = await api.post('/admin/seed/games');
    return response.data;
  },

  // Seed sample categories and tags
  seedSampleData: async (): Promise<SeedResponse> => {
    const response = await api.post('/admin/seed/sample-data');
    return response.data;
  },

  // Get games statistics
  getGamesStats: async (): Promise<{ success: boolean; data: AdminStats }> => {
    const response = await api.get('/admin/stats/games');
    return response.data;
  },

  // Get available import providers
  getImportProviders: async (): Promise<{ success: boolean; providers: string[] }> => {
    const response = await api.get('/admin/import/providers');
    return response.data;
  },

  // Import games from GamePix (New unified endpoint)
  importFromGamePix: async (options: GamePixImportOptions): Promise<GameImportResponse> => {
    const response = await api.post('/admin/import/gamepix', options);
    return response.data;
  },

  // Import games from GameMonetize (New unified endpoint)
  importFromGameMonetize: async (options: GameMonetizeImportOptions): Promise<GameImportResponse> => {
    const response = await api.post('/admin/import/gamemonetize', options);
    return response.data;
  },

  // Import games from GameDistribution
  importFromGameDistribution: async (options: GameDistributionImportOptions): Promise<GameImportResponse> => {
    const response = await api.post('/admin/import/gamedistribution', options);
    return response.data;
  },

  // Trigger scheduled import manually
  triggerScheduledImport: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/admin/import/trigger-scheduled');
    return response.data;
  },

  // Get auto-import configuration
  getAutoImportConfig: async (): Promise<{
    success: boolean;
    data: {
      gamePix: {
        enabled: boolean;
        sid: string | null;
        maxPages: number;
        gamesPerPage: number;
        minQuality: number;
        orderBy: 'pubdate' | 'default';
        schedule: string;
      };
      gameMonetize: {
        enabled: boolean;
        maxGames: number;
        maxPages: number;
        schedule: string;
      };
    };
  }> => {
    const response = await api.get('/admin/import/auto-import/config');
    return response.data;
  },

  // Update auto-import configuration
  updateAutoImportConfig: async (config: {
    gamePix?: {
      enabled?: boolean;
      sid?: string;
      maxPages?: number;
      gamesPerPage?: number;
      minQuality?: number;
      orderBy?: 'pubdate' | 'default';
    };
    gameMonetize?: {
      enabled?: boolean;
      maxGames?: number;
      maxPages?: number;
    };
  }): Promise<{
    success: boolean;
    message: string;
    data: {
      gamePix: {
        enabled: boolean;
        sid: string | null;
        maxPages: number;
        gamesPerPage: number;
        minQuality: number;
        orderBy: 'pubdate' | 'default';
        schedule: string;
      };
      gameMonetize: {
        enabled: boolean;
        maxGames: number;
        maxPages: number;
        schedule: string;
      };
    };
  }> => {
    const response = await api.patch('/admin/import/auto-import/config', config);
    return response.data;
  },

  // Get import logs
  getImportLogs: async (): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      timestamp: string;
      type: 'scheduled' | 'manual';
      provider: 'gamepix' | 'gamemonetize' | 'all';
      status: 'success' | 'error' | 'running';
      message: string;
      details?: {
        imported?: number;
        failed?: number;
        skipped?: number;
        error?: string;
      };
    }>;
    total: number;
  }> => {
    const response = await api.get('/admin/import/logs');
    return response.data;
  },

  // Get comprehensive dashboard statistics
  getDashboardStats: async (): Promise<{
    success: boolean;
    data: {
      overview: {
        totalGames: number;
        approvedGames: number;
        pendingGames: number;
        draftGames: number;
        totalCategories: number;
        totalTags: number;
        totalUsers: number;
        activeUsers: number;
        totalCreators: number;
        totalSessions: number;
        monthlyPlays: number;
        totalPlayCount: number;
        avgPlaysPerGame: number;
        totalLikes: number;
        avgRating: number;
        featuredGames: number;
      };
      topGames: Array<{
        id: string;
        name: string;
        slug: string;
        iconUrl?: string;
        playCount: number;
        likeCount: number;
        rating: number;
        status?: string;
        developer?: string;
      }>;
      topUsers: Array<{
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        totalPlaysReceived: number;
        createdGamesCount: number;
      }>;
      topActiveUsers: Array<{
        userId: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
        sessionCount: number;
        lastActive?: string;
      }>;
      gamesByStatus: Array<{
        status: string;
        count: number;
      }>;
      recentActivity: Array<{
        id: string;
        type?: 'play' | 'game_created' | 'game_approved' | 'user_registered';
        gameId?: string;
        gameName?: string;
        userId?: string;
        username: string;
        action?: string;
        createdAt: string;
      }>;
    };
  }> => {
    const response = await api.get('/admin/stats/dashboard');
    return response.data;
  },

  // Storage Connector APIs
  getStorageConnectors: async (): Promise<{ success: boolean; data: StorageConnector[] }> => {
    const response = await api.get('/admin/storage-connectors');
    return response.data;
  },

  createStorageConnector: async (
    data: StorageConnectorFormData
  ): Promise<{ success: boolean; message: string; data: StorageConnector }> => {
    const response = await api.post('/admin/storage-connectors', data);
    return response.data;
  },

  updateStorageConnector: async (
    id: string,
    data: Partial<StorageConnectorFormData>
  ): Promise<{ success: boolean; message: string; data: StorageConnector }> => {
    const response = await api.put(`/admin/storage-connectors/${id}`, data);
    return response.data;
  },

  deleteStorageConnector: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/admin/storage-connectors/${id}`);
    return response.data;
  },

  activateStorageConnector: async (
    id: string
  ): Promise<{ success: boolean; message: string; data: StorageConnector }> => {
    const response = await api.post(`/admin/storage-connectors/${id}/activate`);
    return response.data;
  },

  testStorageConnector: async (
    data: Partial<StorageConnectorFormData> & { id?: string }
  ): Promise<{ success: boolean; message: string; details?: string }> => {
    const response = await api.post('/admin/storage-connectors/test', data);
    return response.data;
  },

  // Ad Management
  getAdConfigs: async (): Promise<{ success: boolean; data: AdConfigItem[] }> => {
    const response = await api.get('/admin/ads/configs');
    return response.data;
  },

  updateAdConfig: async (
    id: string,
    data: Partial<AdConfigItem>
  ): Promise<{ success: boolean; message: string; data: AdConfigItem }> => {
    const response = await api.put(`/admin/ads/configs/${id}`, data);
    return response.data;
  },

  toggleAdConfig: async (
    id: string
  ): Promise<{ success: boolean; message: string; data: AdConfigItem }> => {
    const response = await api.post(`/admin/ads/configs/${id}/toggle`);
    return response.data;
  },

  bulkUpdateAdConfigs: async (
    data: BulkUpdateAdConfigsPayload
  ): Promise<{ success: boolean; message: string; data: { count: number } }> => {
    const response = await api.post('/admin/ads/configs/bulk', data);
    return response.data;
  },

  // Bundled Games Sync APIs
  getBundledGamesStatus: async (): Promise<BundledGamesStatusResponse> => {
    const response = await api.get('/admin/bundled-games/status');
    return response.data;
  },

  syncBundledGames: async (options?: BundledGamesSyncOptions): Promise<BundledGamesSyncResponse> => {
    const response = await api.post('/admin/bundled-games/sync', options || {});
    return response.data;
  },
};

export interface BundledGameItem {
  slug: string;
  name: string;
  genre: string;
  categorySlug: string;
  localFolderExists: boolean;
  inDatabase: boolean;
  databaseId: string | null;
  status: string;
  gameUrl: string | null;
  iconUrl: string | null;
}

export interface BundledGamesStatusResponse {
  success: boolean;
  data: {
    totalBundled: number;
    syncedInDb: number;
    games: BundledGameItem[];
    gamesDirectory: string;
    gamesDirectoryExists: boolean;
    detectedFrontendUrl?: string;
    activeStoragePublicUrl?: string;
  };
}

export interface BundledGamesSyncOptions {
  seedDb?: boolean;
  uploadStorage?: boolean;
  frontendBaseUrl?: string;
}

export interface BundledGamesSyncResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    message: string;
    database?: {
      success: boolean;
      inserted: number;
      updated: number;
      total: number;
    };
    storage?: {
      success: boolean;
      bucketName?: string;
      providerName?: string;
      gamesCount?: number;
      uploadedGames?: string[];
      totalFiles?: number;
      sizeMb?: string;
      error?: string;
      message?: string;
    };
  };
}


export interface AdConfigItem {
  id: string;
  platform: 'web' | 'android' | 'ios' | 'all';
  provider: 'adsense' | 'admob' | 'custom';
  placementKey: string;
  appOrClientId: string;
  slotOrUnitId?: string;
  format: string;
  isActive: boolean;
  isTestMode: boolean;
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface BulkUpdateAdConfigsPayload {
  configs: Array<{
    platform: string;
    provider: string;
    placementKey: string;
    appOrClientId: string;
    slotOrUnitId?: string;
    format?: string;
    isActive?: boolean;
    isTestMode?: boolean;
    metadata?: Record<string, any>;
  }>;
  globalEnabled?: boolean;
  globalTestMode?: boolean;
}

export interface StorageConnector {
  id: string;
  name: string;
  providerType: 'cloudflare-r2' | 'minio' | 'aws-s3' | 'custom-s3';
  endpoint: string;
  port: number;
  useSSL: boolean;
  accessKey: string;
  secretKeyMasked?: string;
  bucketName: string;
  region: string;
  publicUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StorageConnectorFormData {
  name: string;
  providerType: 'cloudflare-r2' | 'minio' | 'aws-s3' | 'custom-s3';
  endpoint: string;
  port?: number;
  useSSL?: boolean;
  accessKey: string;
  secretKey?: string;
  bucketName: string;
  region?: string;
  publicUrl?: string;
  isActive?: boolean;
}

